import {
  Button,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import { FileDownload, FileUpload } from "@mui/icons-material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfigLoad from "./ConfigLoad";
import EncompassService from "../../../services/encompassService";
import { useActions } from "../../../hooks/useActions";
import { RootState } from "../../../state/store";
import { useSelector } from "react-redux";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import {
  BundlingOption,
  IDocumentBundling,
  IDocumentHandlingPackageConfiguration,
  IEventsBundling,
  IMergerPackageConfiguration,
  SignedOrUnsigned,
} from "../../../models/configuration/importExport/IDocumentBundling";
import {
  IDocumentBundle,
  IEventBundle,
} from "../../../models/configuration/bundles/IBundles";
import {
  IDocumentMap,
  IDocumentMapsConfiguration,
} from "../../../models/configuration/importExport/IDocumentMap";
import { IDocumentMap as IDocumentMapConfig, IDocumentMappingConfiguration } from "../../../models/configuration/bundles/IDocumentMappings";
import { IntentToProceedIngestionOption, IPushbackConfiguration, ReceivedDatePopulation } from "../../../models/configuration/importExport/IPushbackConfiguration";
import ConfirmDialog from "../ConfirmDialog";
import { IImportExportPluginConfiguration } from "../../../models/configuration/importExport/IImportExportPluginConfiguration";
import { saveImportPluginConfig, fetchImportPluginConfig } from "../../../state/slices/importPluginConfigSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../state/store";
import { importIntegration } from "../../../state/slices/importIntegrationSlice";
import { saveImportPushbackConfig } from "../../../state/slices/importPushbackSlice";
import { importXpathFieldMap } from "../../../state/slices/importXpathFieldMapSlice";
import AlertSnackbar from "../AlertSnackbar";
import { importBundlingConfig } from "../../../state/slices/importBundlingSlice";
import { importEventBundlingConfig } from "../../../state/slices/importEventBundlingSlice";
import { importDocumentMappingConfig } from "../../../state/slices/importDocumentMappingSlice";
import { FullFillmentShippingOptions, RequestType, RequestTypeAvailability, SaveLoanBeforeDisclosingOptions, ICtrlToolTips } from "../../../models/configuration/importExport/IPluginConfig";
import { setLoadingState } from "../../../state/appSlice";
import { StyledHeaderButton } from "../../../layout/Header.styles";
import { DtConfirmDialog } from "../../ui/dialog";

interface configTopBarProps {
  error?: string | null;
}

const ConfigTopBar: React.FC<configTopBarProps> = ({
  error
}) => {
  const { getConfigBundleDocumentData, getConfigBundleEventData, getConfigPushbackData, getConfigXPathFieldMapData, getDocumentMappingData } = useActions();
  const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);

  const hasFetchedData = useRef<boolean>(false);
  const { config: pluginConfigData } = useTypedSelector((state) => state.importPluginConfig);
  const { data: documentBundleData } = useTypedSelector((state) => state.configDocumentBundles);
  const { data: eventBundleData } = useTypedSelector((state) => state.configEventBundles);
  const { data: pushbackData } = useTypedSelector((state) => state.configPushbackDefaults);
  const { data: xpathFieldMapData } = useTypedSelector((state) => state.configXPathFieldMap);
  const { data: documentMappingData } = useTypedSelector((state) => state.configDocumentMapping);

  const { loadingSave: importPluginLoading, error: importPluginError} = useTypedSelector((state) => state.importPluginConfig);
  const { loading: importIntegrationLoading, error: importIntegrationError } = useTypedSelector((state) => state.importIntegration);
  const { loading: importPushbackLoading, error: importPushbackError } = useTypedSelector((state) => state.importPushback);
  const { loading: xpathFieldMapLoading, error: importXpathFieldMapError } = useTypedSelector((state) => state.importXpathFieldMap);
  const { loading: importDocumentMappingLoading, error: importDocumentMappingError } = useTypedSelector((state) => state.importDocumentMapping);
  const { loading: importBundlingLoading, error: importBundlingError } = useTypedSelector((state) => state.importBundling);
  const { loading: importEventBundlingLoading, error: importEventBundlingError } = useTypedSelector((state) => state.importEventBundling);

  const [exportClicked, setExportClicked] = useState<boolean>(false);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [openImport, setOpenImport] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info" | "warning">("success");
  const [importError, setImportError] = useState<string[]>([]);
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (
      hasFetchedData.current && 
      exportClicked &&
      pluginConfigData &&
      documentBundleData &&
      eventBundleData &&
      pushbackData &&
      xpathFieldMapData &&
      documentMappingData
    ) {      
      saveConfigAsJson();
    }
    
    // Add timeout to prevent loader from hanging indefinitely
    if (exportClicked) {
      const timeout = setTimeout(() => {
        if (exportClicked) {
          dispatch(setLoadingState({ loading: false }));
          setExportClicked(false);
          showAlert("Export timed out. Please try again.", "error");
        }
      }, 30000); // 30 second timeout
      
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pluginConfigData,
    documentBundleData,
    eventBundleData,
    pushbackData,
    xpathFieldMapData,
    documentMappingData,
    exportClicked
  ]);

  useEffect(() => {
    const isLoading = importPluginLoading || importIntegrationLoading || importBundlingLoading || importEventBundlingLoading || importPushbackLoading || xpathFieldMapLoading || importDocumentMappingLoading;
    setImportLoading(isLoading);
    if (!isLoading && importLoading && importError.length === 0) {
      showAlert("Configuration imported successfully! Refresh the page to see the update", "success");
    } else if(!isLoading && importLoading && importError.length > 0) {
      showAlert("Configuration imported with Error, Please contact Administrator", "warning");
    }
  }, [
    importPluginLoading,
    importIntegrationLoading,
    importBundlingLoading,
    importEventBundlingLoading,
    importPushbackLoading,
    xpathFieldMapLoading,
    importDocumentMappingLoading,
    importLoading,
    importError
  ]);

  useEffect(() => {    
    const importErrors = [
      importPluginError,
      importIntegrationError,
      importBundlingError,
      importEventBundlingError,
      importPushbackError,
      importXpathFieldMapError,
      importDocumentMappingError,
    ].filter((error): error is string => error !== null);

    setImportError(importErrors);
  }, [
    importPluginError,
    importIntegrationError,
    importBundlingError,
    importEventBundlingError,
    importPushbackError,
    importXpathFieldMapError,
    importDocumentMappingError,
  ]);

  const fetchData = useCallback(
    async (configId: string, fetchFunction: (configId: string, accessToken: string | null) => void) => {
      fetchFunction(configId, accessToken);
    },
    [accessToken]
  );

  const getConfigId = () => {
    if (selectedConfigId) return selectedConfigId;
    return EncompassService.getConfigId() as string;
  };

  const initializeData = async () => {
    const configId = getConfigId();
    setSelectedConfigId(configId);
    await Promise.all([
      dispatch(fetchImportPluginConfig(configId)),
      fetchData(configId, getConfigBundleDocumentData),
      fetchData(configId, getConfigBundleEventData),
      fetchData(configId, getConfigPushbackData),
      fetchData(configId, getConfigXPathFieldMapData),
      fetchData(configId, getDocumentMappingData),
    ]);
    hasFetchedData.current = true;
  };

  const exportConfig = async () => {
    try {
      dispatch(setLoadingState({ loading: true, loadingMessage: "Exporting Configuration" }));
      setExportClicked(true);
      await initializeData();
    } catch (error) {
      console.error("Error during export initialization:", error);
      dispatch(setLoadingState({ loading: false }));
      setExportClicked(false);
      showAlert("Failed to initialize export data.", "error");
    }
  };

  const createModifiedData = () => {
    const modifiedIntegrationData = {
      ConformX: { 
        ApiEndpointUrl: "", 
        ApiKey: "", 
        ApiSecret: "" 
      },
      Encompass: { 
        Username: "", 
        Password: "" 
      },
      MaxConcurrency: 0,
      BaseUrl: null
    };
        
    const mapDocumentBundleData = (pkg: IDocumentBundle): IDocumentBundling => ({
      PackageId: pkg.PackageId ?? "",
      Signature: SignedOrUnsigned[pkg.BundlingConfigurationType as keyof typeof SignedOrUnsigned],
      BundlingOption: BundlingOption[pkg.BundlingOption as keyof typeof BundlingOption],
      OverrideConfig: pkg.DocumentHandlingPackageConfigs.length > 0,
      MergeList: pkg.MergerPackageConfigs.length > 0,
      DocumentHandlingPackageConfigs: pkg.DocumentHandlingPackageConfigs.map((obj)=> {
        return(
          {
            DocutechIndex: obj.DocumentIndex,
            DocutechForm: obj.DocumentName,
            EncompassFolder: obj.EncompassFolder,
            Suffix: obj.Suffix,
            ReuseFolder: obj.ReuseFolder,
            EncompassAttachment: obj.EncompassAttachment,
          }
        )
      }) as IDocumentHandlingPackageConfiguration[],
      MergerPackageConfigs: pkg?.MergerPackageConfigs.map((obj)=> {
        return(
          {
            MergeList: obj.MergeList,
            MergeListConcatenated: obj.MergeList?.join(),
            EncompassFolder: obj.EncompassFolder,
            ReuseFolder: obj.ReuseFolder,
            EncompassAttachment: obj.EncompassAttachment,
            Suffix: obj.Suffix
          }
        )
      }) as IMergerPackageConfiguration[] ,
      AllowMergerConfigDuplicate: pkg.MergerConfigDuplicate ?? false,
      AllowDocumentHandlingDuplicate: pkg.DocumentHandlingDuplicate ?? false,
    });

    const modifiedDocumentBundleData: IDocumentBundling[] = (
      documentBundleData?.Configuration?.Bundling ?? []
    ).map(mapDocumentBundleData);

    const modifiedEventBundleData: IEventsBundling[] = (
      eventBundleData?.Configuration?.Bundling ?? []
    ).map((event: IEventBundle): IEventsBundling => ({
      EventName: event.EventName ?? "",
      DocumentPackageHandling: (event.Configuration ?? []).map(mapDocumentBundleData),
    }));

    const modifiedDocumentMapData: IDocumentMapsConfiguration = {
      DateSaved: documentMappingData?.Configuration?.DateSaved ?? "",
      DocumentMaps: (
        documentMappingData?.Configuration?.DocumentMaps ?? []
      ).map((map: IDocumentMapConfig): IDocumentMap => ({
        index: map.DocumentIndex,
        Docutech: map.ConformXDocumentName,
        Encompass: map.EncompassDocumentName,
      })),
    };

    const modifiedPushbackData: IPushbackConfiguration = {
      IsDefault: pushbackData?.Configuration.IsDefault ?? false,
      ReceivedDatePopulation: ReceivedDatePopulation[pushbackData?.Configuration.ReceivedDatePopulation as keyof typeof ReceivedDatePopulation],
      UnnamedDocumentTitle: pushbackData?.Configuration.UnnamedDocumentTitle ?? "",
      MaxMessageRetryCount: Number(pushbackData?.Configuration.MaxMessageRetryCount) ?? 0,
      MessageRetryVisibilityTimeoutSeconds: Number(pushbackData?.Configuration.MessageRetryVisibilityTimeoutSeconds) ?? 0,
      LoanLockedVisibilityTimeoutSeconds: Number(pushbackData?.Configuration.LoanLockedVisibilityTimeoutSeconds) ?? 0,
      MaxConcurrency: Number(pushbackData?.Configuration.MaxConcurrency) ?? 0,
      DisclosureTrackingMapping: {
        IntentToProceed: {
          DocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.IntentToProceed?.DocumentIds ?? [],
          Ingestion: IntentToProceedIngestionOption[pushbackData?.Configuration.DisclosureTrackingMapping?.IntentToProceed?.Ingestion as keyof typeof IntentToProceedIngestionOption],
        },
        AffiliatedBusinessDisclosureDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.AffiliatedBusinessDisclosureDocumentIds ?? [],
        CHARMBookletDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.CHARMBookletDocumentIds ?? [],
        SpecialInfoBookletDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.SpecialInfoBookletDocumentIds ?? [],
        HELOCBrochureDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.HELOCBrochureDocumentIds ?? [],
        AppraisalDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.AppraisalDocumentIds ?? [],
        AVMDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.AVMDocumentIds ?? [],
        HomeCounselingDisclosureDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.HomeCounselingDisclosureDocumentIds ?? [],
        HighCostDisclosureDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.HighCostDisclosureDocumentIds ?? [],
        ProviderListDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.ProviderListDocumentIds ?? [],
        SafeHarborDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.SafeHarborDocumentIds ?? [],
        LoanEstimateDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.LoanEstimateDocumentIds ?? [],
        ClosingDisclosureDocumentIds: pushbackData?.Configuration.DisclosureTrackingMapping?.ClosingDisclosureDocumentIds ?? [],
      },
    };

    const tempPluginObj = {
      ...pluginConfigData?.Configuration,
      Packages: pluginConfigData?.Configuration?.Packages.map((pkg) => {
        return {
          Group: pkg.Group,
          Order: pkg.Order,
          AltPkgId: pkg.AlternatePackageId ?? "",
          ParentPkgId: pkg.ParentPackageId ?? "",
          ToolTipText: pkg.ToolTipText ?? "",
          ValidDeliveryTypes: pkg.ValidDeliveryTypes,
          AutoSelectWhen: pkg.AutoSelectWhen,
          Id: pkg.Id,
          Caption: pkg.Caption,
          Enabled: pkg.Enabled,
          Expressions: pkg.Expressions,
          Roles: pkg.Roles,
          Users: pkg.Users,
          CustomAttributes: pkg.CustomAttributes
        }}),      
        _DefaultValues: {
          ...pluginConfigData?.Configuration?.DefaultValues,
          AltPackageColor: pluginConfigData?.Configuration?.DefaultValues?.AlternatePackageColor ?? "Black",
          FulfillmentShippingMethod: FullFillmentShippingOptions[pluginConfigData?.Configuration?.DefaultValues?.FulfillmentShippingMethod as unknown as keyof typeof FullFillmentShippingOptions],
          RequestTypeAvailability: RequestTypeAvailability[pluginConfigData?.Configuration?.DefaultValues?.RequestTypeAvailability as unknown as keyof typeof RequestTypeAvailability],
          DefaultRequestType: RequestType[pluginConfigData?.Configuration?.DefaultValues?.DefaultRequestType as unknown as keyof typeof RequestType],
          SaveLoanBeforeDisclosing: SaveLoanBeforeDisclosingOptions[pluginConfigData?.Configuration?.DefaultValues?.SaveLoanBeforeDisclosing as unknown as keyof typeof SaveLoanBeforeDisclosingOptions],
        },
      FrmToolTips: pluginConfigData?.Configuration?.FormToolTips.map((e: ICtrlToolTips) => {
        return { CtrlId: e.ControlId, ToolTip: e.ToolTip }
      }),
      ErrorMessageOverrides: pluginConfigData?.Configuration?.ErrorMessageOverrides.map((errorMessageOverrides)=> {
        return {
          RespMessage: errorMessageOverrides.ResponseMessage, // DTHosted has same property name
          RespOverride: errorMessageOverrides.ResponseOverride,
        }
      }) ?? [],
      DisclosureTrackingMapping: {
        ...pluginConfigData?.Configuration?.DisclosureTrackingMapping,
        IntentToProceed: {
          DocumentIds: pluginConfigData?.Configuration?.DisclosureTrackingMapping.IntentToProceed.DocumentIds,
          Ingestion: IntentToProceedIngestionOption[pluginConfigData?.Configuration?.DisclosureTrackingMapping.IntentToProceed.Ingestion as unknown as keyof typeof IntentToProceedIngestionOption],
        },
      },
    }
    
    delete tempPluginObj.DefaultValues;
    delete tempPluginObj.FormToolTips;

    const modifiedPluginData = {
      ...tempPluginObj
    }

    return {
      ...pluginConfigData,
      PluginConfiguration: modifiedPluginData,
      IntegrationApi: modifiedIntegrationData,
      DocumentBundling: modifiedDocumentBundleData,
      EventTypeBundling: modifiedEventBundleData,
      PushbackProcessing: modifiedPushbackData,
      XpathFieldMapping: xpathFieldMapData?.Configuration,
      DocumentMapping: modifiedDocumentMapData,
    };
  };

  const handleSnackBarClose = () => {
    setAlertOpen(false);
  };

  const showAlert = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const saveConfigAsJson = () => {
    try {
      const modifiedData = createModifiedData();
      delete modifiedData.Configuration;
      const json = JSON.stringify(modifiedData, null, 2);
      
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "config.json";
      link.click();
      URL.revokeObjectURL(url);
      
      showAlert("Configuration exported successfully!", "success");
    } catch (error) {
      console.error("Error during export:", error);
      showAlert("Failed to export configuration.", "error");
    } finally {
      // Always clear the loading states regardless of success or failure
      setExportClicked(false);
      dispatch(setLoadingState({ loading: false }));
    }
  };

  const importConfig = () => {    
    setOpenImport(true);
  };

  const mapDocumentBundle = (bundle: IDocumentBundling): IDocumentBundle => ({
    PackageId: bundle.PackageId,
    BundlingConfigurationType: SignedOrUnsigned[bundle.Signature],
    BundlingOption: BundlingOption[bundle.BundlingOption],
    MergerConfigDuplicate: bundle.AllowMergerConfigDuplicate,
    DocumentHandlingDuplicate: bundle.AllowDocumentHandlingDuplicate,
    OverrideConfigExists: bundle.OverrideConfig,
    DocumentHandlingPackageConfigs: bundle.DocumentHandlingPackageConfigs.map((config) => ({
      DocumentIndex: config.DocutechIndex,
      DocumentName: config.DocutechForm,
      EncompassFolder: config.EncompassFolder,
      Suffix: config.Suffix,
      ReuseFolder: config.ReuseFolder,
      EncompassAttachment: config.EncompassAttachment,
    })),
    MergerPackageConfigs: bundle.MergerPackageConfigs.map((config) => ({
      MergeList: config.MergeList,
      EncompassFolder: config.EncompassFolder,
      Suffix: config.Suffix,
      ReuseFolder: config.ReuseFolder,
      EncompassAttachment: config.EncompassAttachment,
    })),
  });

  const importConfigHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const configId = getConfigId();
    const file = event.target.files?.[0];
    if (file && file.type === "application/json") {      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedConfig = JSON.parse(e.target?.result as string) as IImportExportPluginConfiguration;
          
          if(parsedConfig.PluginConfiguration) {
            const updatedPluginData: any = {              
                ...parsedConfig.PluginConfiguration,
                Packages: parsedConfig.PluginConfiguration.Packages.map((pkg) => ({
                  Group: pkg.Group,
                  Order: pkg.Order,
                  AlternatePackageId: pkg.AltPkgId,
                  ParentPackageId: pkg.ParentPkgId,
                  ToolTipText: pkg.ToolTipText,
                  ValidDeliveryTypes: pkg.ValidDeliveryTypes,
                  AutoSelectWhen: pkg.AutoSelectWhen,
                  Id: pkg.Id,
                  Caption: pkg.Caption,
                  Enabled: pkg.Enabled,
                  Expressions: pkg.Expressions,
                  Roles: pkg.Roles,
                  Users: pkg.Users,
                  CustomAttributes: pkg.CustomAttributes
                })),
                DefaultValues: {
                  ...parsedConfig.PluginConfiguration._DefaultValues,
                  FulfillmentShippingMethod: typeof parsedConfig.PluginConfiguration._DefaultValues.FulfillmentShippingMethod === "string"
                    ? FullFillmentShippingOptions[parsedConfig.PluginConfiguration._DefaultValues.FulfillmentShippingMethod as keyof typeof FullFillmentShippingOptions]
                    : parsedConfig.PluginConfiguration._DefaultValues.FulfillmentShippingMethod,
                  RequestTypeAvailability: typeof parsedConfig.PluginConfiguration._DefaultValues.RequestTypeAvailability === "string"
                    ? RequestTypeAvailability[parsedConfig.PluginConfiguration._DefaultValues.RequestTypeAvailability as keyof typeof RequestTypeAvailability]
                    : parsedConfig.PluginConfiguration._DefaultValues.RequestTypeAvailability,
                  DefaultRequestType: typeof parsedConfig.PluginConfiguration._DefaultValues.DefaultRequestType === "string"
                    ? RequestType[parsedConfig.PluginConfiguration._DefaultValues.DefaultRequestType as keyof typeof RequestType]
                    : parsedConfig.PluginConfiguration._DefaultValues.DefaultRequestType,
                  SaveLoanBeforeDisclosing: typeof parsedConfig.PluginConfiguration._DefaultValues.SaveLoanBeforeDisclosing === "string"
                    ? SaveLoanBeforeDisclosingOptions[parsedConfig.PluginConfiguration._DefaultValues.SaveLoanBeforeDisclosing as keyof typeof SaveLoanBeforeDisclosingOptions]
                    : parsedConfig.PluginConfiguration._DefaultValues.SaveLoanBeforeDisclosing,
                },
                ErrorMessageOverrides: parsedConfig.PluginConfiguration.ErrorMessageOverrides.map((errMsgOverrides) => ({
                  ResponseMessage: errMsgOverrides.RespMessage,
                  ResponseOverride: errMsgOverrides.RespOverride,
                }))
            }
            dispatch(saveImportPluginConfig({ configurationId: configId, config: updatedPluginData }));
          }
          if(parsedConfig.IntegrationApi) {           
            dispatch(importIntegration({ configId: configId, integration: parsedConfig.IntegrationApi }));
          }
          if(parsedConfig.DocumentBundling) {
            const documentBundles: IDocumentBundle[] = parsedConfig.DocumentBundling.map(mapDocumentBundle);
            dispatch(importBundlingConfig({ configurationId: configId, config: documentBundles }));
          }
          if (parsedConfig.EventTypeBundling) {            
            const eventBundles: IEventBundle[] = parsedConfig.EventTypeBundling.map(event => ({
              EventName: event.EventName,
              Configuration: event.DocumentPackageHandling.map(mapDocumentBundle),
            }));
            dispatch(importEventBundlingConfig({ configurationId: configId, config: eventBundles }));
          }
          if(parsedConfig.PushbackProcessing) {            
            const updatedPushbackDefaultData: IPushbackConfiguration = {
              ...parsedConfig.PushbackProcessing,
              ReceivedDatePopulation: ReceivedDatePopulation[parsedConfig.PushbackProcessing.ReceivedDatePopulation as unknown as keyof typeof ReceivedDatePopulation],
              DisclosureTrackingMapping: {
                ...parsedConfig.PushbackProcessing.DisclosureTrackingMapping,
                IntentToProceed: {
                  DocumentIds: parsedConfig.PushbackProcessing.DisclosureTrackingMapping.IntentToProceed.DocumentIds,
                  Ingestion: IntentToProceedIngestionOption[parsedConfig.PushbackProcessing.DisclosureTrackingMapping.IntentToProceed.Ingestion as unknown as keyof typeof IntentToProceedIngestionOption],
                },
              },
            }
            dispatch(saveImportPushbackConfig({ configurationId: configId, config: updatedPushbackDefaultData }));
          }
          if(parsedConfig.XpathFieldMapping) {           
            dispatch(importXpathFieldMap({ configurationId: configId, xPathfieldMap: parsedConfig.XpathFieldMapping }));
          }
          if(parsedConfig.DocumentMapping) {
            const updatedDocumentMappingData: IDocumentMappingConfiguration = {
              configurationId: configId,
              Configuration: {
                DateSaved: parsedConfig.DocumentMapping.DateSaved ?? new Date().toUTCString(),
                DocumentMaps: parsedConfig.DocumentMapping.DocumentMaps.map((map) => ({
                  DocumentIndex: map.index,
                  ConformXDocumentName: map.Docutech,
                  EncompassDocumentName: map.Encompass,
                })),
              },
            }
            dispatch(importDocumentMappingConfig({ configurationId: configId, config: updatedDocumentMappingData }));
          }
          
        } catch (error) {
          console.error("Error parsing JSON file", error);
          showAlert("Failed to import configuration.", "error");
        }
      };
      reader.readAsText(file);
    } else {
      console.error("Please select a valid JSON file.");
      showAlert("Please select a valid JSON file.", "error");
    }
  };

  return (
    <>
      {importLoading && (
        <ConfigLoad loading={importLoading} message="Importing Configuration" />
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        {error && (
          <Tooltip title={error} arrow>
            <Typography
              sx={{
                fontSize: ".7rem",
                padding: 1,
                border: "1px solid red",
                borderRadius: "2px",
                color: "red"
              }}
              variant="body1"
            >
              Error
            </Typography>
          </Tooltip>
        )}
        <StyledHeaderButton
          startIcon={<FileUpload />}
          onClick={exportConfig}
          size='small'
        >
          Export
        </StyledHeaderButton>
        <StyledHeaderButton 
          startIcon={<FileDownload />}
          onClick={importConfig}
          size='small'
        >
          Import
        </StyledHeaderButton>
        <input
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          id="import-config-file"
          onChange={(event) => {
            importConfigHandler(event);
            event.target.value = "";
          }}
        />
      </Stack>
      <DtConfirmDialog
        maxWidth="xs"
        open={openImport}
        title="Import Warning"
        message="Importing a configuration will overwrite any configuration that is saved. Press 'Yes' to continue."
        onConfirm={() => {
          setOpenImport(false);
          document.getElementById('import-config-file')?.click();
        }}
        onClose={() => setOpenImport(false)}
        confirmLabel="Yes"
        cancelLabel="No"
      />
      <AlertSnackbar
        alertOpen={alertOpen}
        alertMessage={alertMessage}
        alertSeverity={alertSeverity}
        handleSnackBarClose={handleSnackBarClose}
      />
    </>
  );
};

export default ConfigTopBar;
